import { styled, Card, CardContent, Chip, Box } from '@mui/material';
import { grey, red } from '@mui/material/colors';

export const PLAN_FLEX_COLORS = {
  included: '#7E6BD6',
  available: '#D8CFF0',
  overage: '#E91E63',
  overageSurface: '#FDEEF3',
  nextPaymentSurface: '#F3EEFB',
};

export const SubscriptionPlanFlexCardRoot = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
}));

export const SubscriptionPlanFlexCardHeader = styled('div')(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  paddingBottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexDirection: 'row',
  gap: theme.spacing(1),
}));

export const SubscriptionPlanFlexCardHeaderStatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isExpired' && prop !== 'hasRenewal',
})<{ isExpired: boolean; hasRenewal?: boolean }>(
  ({ theme, isExpired, hasRenewal }) => ({
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightBold,
    minWidth: 100,
    backgroundColor: '#C0FFE2',
    color: '#009245',
    borderColor: '#09D177',

    ...(isExpired && {
      backgroundColor: grey[200],
      color: grey[700],
      borderColor: grey[500],
    }),

    ...(hasRenewal && {
      backgroundColor: red[200],
      color: red[700],
      borderColor: red[500],
    }),
  }),
);

export const SubscriptionPlanFlexCardContent = styled(CardContent)(
  ({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),

    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  }),
);

// --- Metadata grid ---------------------------------------------------------

export const SubscriptionPlanFlexMetaGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  columnGap: theme.spacing(2),
  rowGap: theme.spacing(0.5),
}));

export const SubscriptionPlanFlexMetaGridFullRow = styled('div')({
  gridColumn: '1 / -1',
});

// --- Usage bar -------------------------------------------------------------

export const SubscriptionPlanFlexUsageBarGroup = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

export const SubscriptionPlanFlexUsageBarTrack = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  width: '100%',
  height: 14,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: grey[200],
  overflow: 'hidden',
}));

export const SubscriptionPlanFlexUsageBarIncluded = styled('div', {
  shouldForwardProp: (prop) => prop !== 'widthPercent',
})<{ widthPercent: number }>(({ widthPercent }) => ({
  width: `${widthPercent}%`,
  height: '100%',
  backgroundColor: PLAN_FLEX_COLORS.included,
}));

export const SubscriptionPlanFlexUsageBarAvailable = styled('div', {
  shouldForwardProp: (prop) => prop !== 'widthPercent',
})<{ widthPercent: number }>(({ widthPercent }) => ({
  width: `${widthPercent}%`,
  height: '100%',
  backgroundColor: PLAN_FLEX_COLORS.available,
}));

export const SubscriptionPlanFlexUsageBarOverage = styled('div', {
  shouldForwardProp: (prop) => prop !== 'widthPercent',
})<{ widthPercent: number }>(({ widthPercent }) => ({
  width: `${widthPercent}%`,
  height: '100%',
  backgroundColor: PLAN_FLEX_COLORS.overage,
}));

export const SubscriptionPlanFlexUsageBarLimitMarker = styled('div', {
  shouldForwardProp: (prop) => prop !== 'leftPercent',
})<{ leftPercent: number }>(({ leftPercent }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: `${leftPercent}%`,
  width: 2,
  backgroundColor: '#FFFFFF',
}));

export const SubscriptionPlanFlexUsageBarLimitLabels = styled('div')(
  ({ theme }) => ({
    position: 'relative',
    width: '100%',
    height: 14,
    marginTop: theme.spacing(-0.5),
  }),
);

export const SubscriptionPlanFlexUsageBarLimitLabel = styled('div', {
  shouldForwardProp: (prop) => prop !== 'leftPercent',
})<{ leftPercent: number }>(({ leftPercent }) => ({
  position: 'absolute',
  left: `${leftPercent}%`,
  // Anchor the right edge (the ↑ arrow) at the separator, nudged so the
  // arrow glyph sits centered over the marker line.
  transform: 'translateX(calc(-100% + 0.3em))',
  whiteSpace: 'nowrap',
}));

export const SubscriptionPlanFlexLegendDot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'dotColor',
})<{ dotColor: string }>(({ theme, dotColor }) => ({
  display: 'inline-block',
  width: 12,
  height: 12,
  borderRadius: 3,
  backgroundColor: dotColor,
  marginRight: theme.spacing(0.75),
}));

// --- Summary tiles ---------------------------------------------------------

export const SubscriptionPlanFlexSummaryGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(2),
}));

export const SubscriptionPlanFlexSummaryTile = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'highlight',
})<{ highlight?: boolean }>(({ theme, highlight }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: highlight ? PLAN_FLEX_COLORS.overageSurface : grey[100],
}));

// --- Next payment ----------------------------------------------------------

export const SubscriptionPlanFlexNextPaymentRoot = styled('div')(
  ({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  }),
);

export const SubscriptionPlanFlexNextPaymentHeader = styled('div')(
  ({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    backgroundColor: PLAN_FLEX_COLORS.nextPaymentSurface,
  }),
);

export const SubscriptionPlanFlexNextPaymentBody = styled('div')(
  ({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  }),
);

export const SubscriptionPlanFlexNextPaymentRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
});

// --- Footnote --------------------------------------------------------------

export const SubscriptionPlanFlexFootnote = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: PLAN_FLEX_COLORS.nextPaymentSurface,
  color: theme.palette.text.secondary,

  '& svg': {
    width: '1em',
    height: '1em',
    marginTop: 2,
    flexShrink: 0,
  },
}));
